/**
 * Column definitions for the audit logs DataTable.
 *
 * The activity cell rendering logic uses specialized renderers from activity-renderers.tsx
 * for improved modularity and testability.
 */

import React from 'react';
import { Text, Tooltip } from '@customerio/pluma-components/react';
import { createColumnHelper } from '@tanstack/react-table';
import type { AuditLogEntry } from '../../types';
import { getActivityText, NO_OBJECT_ID_TYPES } from '../../utils/object-type-mapping';
import { lowercasePreservingAcronyms } from '../../utils/formatting';
import { isEmptyString } from '@/utils/string';
import { type UserWithName } from '../../utils/user-formatting';
import {
  getTeamMemberInfo,
  getPermissionTargetMember,
  getEntityName,
  getObjectIdentifier,
  getChangedFieldsText,
  getDesignStudioElementType,
  getLogData,
  renderAccountSystemActivity,
  renderUserAccountPermissionsSystemActivity,
  renderWorkspacePermissionsSystemActivity,
  renderUserSystemActivity,
  renderRoleSystemActivity,
  renderProfileDeletionActivity,
  renderObjectDeletionActivity,
  renderProfileMergeActivity,
  renderPermissionActivity,
  renderPermissionUpdateActivity,
  renderUserActivity,
  renderSelfUpdateActivity,
  renderGenericSystemActivity,
  type ActivityRenderContext,
} from './activity-renderers';
import { isAPITriggeredBroadcast } from '../../utils/campaign-mapping/campaign-types';

const columnHelper = createColumnHelper<AuditLogEntry>();

interface RoleDefinition {
  id: number;
  name: string;
}

/**
 * Determine if the object identifier should be shown in the activity row.
 * @param log - The audit log entry
 * @param isUserEvent - Whether this is a User event
 * @param isPermissionEvent - Whether this is a permission event
 * @param objectName - The object name (empty string means no name)
 * @returns true if the object identifier should be displayed
 */
function shouldShowObjectIdentifier(
  log: AuditLogEntry,
  isUserEvent: boolean,
  isPermissionEvent: boolean,
  objectName: string,
): boolean {
  const shouldHideObjectId = NO_OBJECT_ID_TYPES.has(log.object_type);
  return !isUserEvent && !isPermissionEvent && !isEmptyString(objectName) && !shouldHideObjectId;
}

/**
 * Extended user info for audit logs table.
 * Extends UserWithName with additional fields needed for user lookups.
 */
interface UserInfo extends UserWithName {
  user_id: number;
  deleted?: boolean;
}

interface CreateColumnsOptions {
  context: 'account' | 'workspace';
  getUserDisplayName: (userId: number) => string;
  /** Map of user_id to user info for O(1) lookups */
  usersById: Map<number, UserInfo>;
  roleDefinitions?: Record<number, RoleDefinition> | null;
  /** Segment data for resolving segment IDs to names (e.g., for deleted segments) */
  segments?: Array<{ id: number; name: string }>;
  /** Workspace data for resolving workspace IDs to names (e.g., for WorkspacePermissions) */
  workspaces?: Array<{ id: number; name: string }>;
  /** Newsletter data for resolving newsletter IDs to names */
  newsletters?: Array<{ id: number; name: string }>;
  /** Transactional message data for resolving transactional message IDs to names */
  transactionalMessages?: Array<{ id: number; name: string }>;
  /** Campaign data for resolving campaign IDs to names */
  campaigns?: Array<{ id: number; name: string }>;
  /** Workspace/Account ID for building entity URLs */
  contextId: number;
}

/**
 * Create the table columns for audit logs.
 */
export function createAuditLogColumns({
  context,
  getUserDisplayName,
  usersById,
  roleDefinitions,
  segments = [],
  workspaces = [],
  newsletters = [],
  transactionalMessages = [],
  campaigns = [],
  contextId,
}: CreateColumnsOptions) {
  return [
    columnHelper.display({
      id: 'activity',
      header: 'Activity',
      cell: (info) => {
        const log = info.row.original;
        const userName = getUserDisplayName(log.user_id);
        const { verb, objectName } = getActivityText(log.object_type, log.action, context);
        // For DesignStudioNode, get the specific element type (Email, Component, etc.)
        // For Campaign, check if it's an API-Triggered Broadcast
        let effectiveObjectName = objectName;
        if (log.object_type === 'DesignStudioNode') {
          effectiveObjectName = getDesignStudioElementType(log);
        } else if (log.object_type === 'Campaign') {
          // Check for campaign type in processed data first, then fall back to raw data
          const logData = getLogData(log);
          const rawData = log.data as Record<string, unknown> | null | undefined;
          const campaignType = logData?.type ?? logData?.Type ?? rawData?.type ?? rawData?.Type;
          if (isAPITriggeredBroadcast(campaignType as string | number | null | undefined)) {
            effectiveObjectName = 'API-triggered broadcast';
          }
        }
        // Lowercase object name for Activity display, but preserve acronyms like API, SMS, etc.
        const displayObjectName = lowercasePreservingAcronyms(effectiveObjectName);

        // Get actor's name and email separately for proper formatting
        const actorUser = usersById.get(log.user_id);
        const actorFirstName = actorUser?.first_name?.trim() ?? '';
        const actorLastName = actorUser?.last_name?.trim() ?? '';
        const actorFullName = [actorFirstName, actorLastName].filter(Boolean).join(' ');
        const actorName = actorFullName !== '' ? actorFullName : null;
        const actorEmail = actorUser?.email ?? null;

        // Build context for renderers
        const context_: ActivityRenderContext = {
          log,
          userName,
          actorName,
          actorEmail,
          verb,
          objectName: effectiveObjectName,
          displayObjectName,
          usersById,
          roleDefinitions,
          segments,
          workspaces,
          newsletters,
          contextId,
        };

        // Extract common data using helper functions
        const teamMemberInfo = getTeamMemberInfo(log);
        const isUserEvent = log.object_type === 'User';
        // Check if user is updating their own profile
        // Compare user_id (actor) with object_id (target user) or userId from data
        const objectIdAsNumber = log.object_id != null ? Number(log.object_id) : null;
        const isSelfUpdate =
          isUserEvent &&
          log.user_id != null &&
          ((teamMemberInfo.userId != null && log.user_id === teamMemberInfo.userId) ||
            (objectIdAsNumber != null && !Number.isNaN(objectIdAsNumber) && log.user_id === objectIdAsNumber));
        // Team member info for User events (not self-updates)
        // Include if there's a name OR email (renderUserActivity handles both cases)
        const teamMemberInfoForRender =
          (teamMemberInfo.name != null || teamMemberInfo.email != null) && !isSelfUpdate ? teamMemberInfo : undefined;

        // For permission events (UserAccountPermissions, WorkspacePermissions), get the target team member
        const isPermissionEvent =
          log.object_type === 'UserAccountPermissions' || log.object_type === 'WorkspacePermissions';
        const permissionTargetMember = getPermissionTargetMember(log, usersById);

        // Get entity name and object identifier
        const entityName = getEntityName(log, segments, newsletters, transactionalMessages, campaigns);
        const objectIdentifierValue = getObjectIdentifier(log, entityName, contextId, campaigns);

        // Special handling for System user - use passive voice with extracted renderers
        if (userName === 'System') {
          // Account events
          if (log.object_type === 'Account') {
            return renderAccountSystemActivity(context_);
          }
          // UserAccountPermissions events
          if (log.object_type === 'UserAccountPermissions') {
            return renderUserAccountPermissionsSystemActivity(context_);
          }
          // WorkspacePermissions events
          if (log.object_type === 'WorkspacePermissions' && log.object_id != null) {
            return renderWorkspacePermissionsSystemActivity(context_);
          }
          // User (team member) events
          // Include if there's a name OR email (renderUserSystemActivity handles both cases)
          if (log.object_type === 'User' && (teamMemberInfo.name != null || teamMemberInfo.email != null)) {
            return renderUserSystemActivity(context_);
          }
          // Role events
          if (log.object_type === 'Role') {
            return renderRoleSystemActivity(context_);
          }
          // Generic system events
          return renderGenericSystemActivity(context_, entityName);
        }

        // For non-updated actions, show activity text with object ID
        if (log.action.toLowerCase() !== 'updated') {
          // Permission events (create/delete)
          if (isPermissionEvent && permissionTargetMember != null) {
            return renderPermissionActivity(context_, permissionTargetMember);
          }
          // ProfileDeletion events
          if (log.object_type === 'ProfileDeletion') {
            return renderProfileDeletionActivity(context_);
          }
          // ObjectDeletion events
          if (log.object_type === 'ObjectDeletion') {
            return renderObjectDeletionActivity(context_);
          }
          // ProfileMerge events
          if (log.object_type === 'ProfileMerge') {
            return renderProfileMergeActivity(context_);
          }
          // Standard activity (non-updated)
          const showObjectIdentifier = shouldShowObjectIdentifier(log, isUserEvent, isPermissionEvent, objectName);
          // Add leading space since objectIdentifierValue doesn't include one
          const objectIdentifierPart = showObjectIdentifier ? (
            typeof objectIdentifierValue === 'string' ? (
              ` ${objectIdentifierValue}`
            ) : (
              <> {objectIdentifierValue}</>
            )
          ) : (
            ''
          );
          return renderUserActivity(context_, { teamMemberInfo: teamMemberInfoForRender, objectIdentifierPart });
        }

        // For updated actions, compute changed fields and render
        const changedFieldsText = getChangedFieldsText(log);

        // Self-update: user updating their own profile
        if (isSelfUpdate) {
          return renderSelfUpdateActivity(context_, changedFieldsText);
        }

        // Permission update events
        if (isPermissionEvent && permissionTargetMember != null) {
          return renderPermissionUpdateActivity(context_, permissionTargetMember, changedFieldsText);
        }

        // Standard updated activity
        const showObjectIdentifierForUpdate = shouldShowObjectIdentifier(
          log,
          isUserEvent,
          isPermissionEvent,
          objectName,
        );
        // Add leading space since objectIdentifierValue doesn't include one
        const objectIdentifierPartForUpdate = showObjectIdentifierForUpdate ? (
          typeof objectIdentifierValue === 'string' ? (
            ` ${objectIdentifierValue}`
          ) : (
            <> {objectIdentifierValue}</>
          )
        ) : (
          ''
        );
        return renderUserActivity(context_, {
          teamMemberInfo: teamMemberInfoForRender,
          objectIdentifierPart: objectIdentifierPartForUpdate,
          changedFieldsText,
        });
      },
      enableSorting: false,
      // Activity column uses default width (1fr) to expand
    }),
    columnHelper.accessor('timestamp', {
      id: 'timestamp',
      header: () => (
        <Text size="s" weight="medium" textAlign="right" display="block" whiteSpace="nowrap">
          Timestamp
        </Text>
      ),
      cell: (info) => {
        const timestamp = info.getValue();
        // Handle both Unix timestamps (numbers) and ISO strings
        let date: Date;
        if (typeof timestamp === 'number') {
          // If it's a Unix timestamp in seconds (less than 1e12), convert to milliseconds
          const timestampMs = timestamp < 1e12 ? timestamp * 1000 : timestamp;
          date = new Date(timestampMs);
        } else if (typeof timestamp === 'string') {
          date = new Date(timestamp);
        } else {
          date = new Date(timestamp);
        }

        if (Number.isNaN(date.getTime())) {
          return (
            <Text size="xxs" color="subtle">
              Invalid date
            </Text>
          );
        }

        // Format date and time parts separately
        const dateTimeString = date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
        });

        // Get timezone separately and wrap in brackets
        const timezoneString =
          date
            .toLocaleString('en-US', {
              timeZoneName: 'short',
            })
            .split(' ')
            .pop() ?? '';

        const formatted = !isEmptyString(timezoneString) ? `${dateTimeString} (${timezoneString})` : dateTimeString;

        return (
          <Tooltip content={date.toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'long' })}>
            <Text size="xxs" color="subtle" textAlign="right" display="block" whiteSpace="nowrap">
              {formatted}
            </Text>
          </Tooltip>
        );
      },
      enableSorting: true,
      sortingFn: 'basic',
      meta: {
        // Timestamp column gets a fixed width to fit full timestamp with timezone, right-aligned
        width: 250,
        align: 'end',
      },
    }),
  ];
}
